import { graphql } from 'gatsby';
import React from 'react';
import { css } from '@emotion/react';
import { colors } from '../../primitives/colors';
import { StyledButtonAsLink } from '../../primitives/styled-button';
import Container from '../../primitives/grid/container';
import RichTextEditorStyles from '../../utils/rich-text-editor-styles';
import apCaseOnlyTitleTags from '../../utils/ap-case-only-title-tags';
import { breakpoints } from '../../primitives/tokens';
import Typography from '../../primitives/typography';
import Divider from '../../primitives/divider';
import RowSet from '../../primitives/grid/row-set';
import Row from '../../primitives/grid/row';
import Col from '../../primitives/grid/col';

const styles = {
    container: css({
        paddingTop: '64px',
        paddingBottom: '64px',
        [breakpoints.mb]: {
            paddingTop: '40px',
            paddingBottom: '40px',
        },
    }),
    breadcrumbsWrap: css({
        marginBottom: '12px',
    }),
    headlines: css({
        marginBottom: '64px',
    }),
    headline: css({
        fontSize: '54px',
        marginBottom: '20px',
        lineHeight: 1,
        textAlign: 'left',
        [breakpoints.tb]: {
            fontSize: '36px',
        },
    }),
    subheadline: css({
        maxWidth: '768px',
        fontSize: '20px',
        lineHeight: '28px',
        marginRight: 'auto',
        marginLeft: 'auto',
        marginBottom: '20px',
        textAlign: 'center',
        [breakpoints.tb]: {
            fontSize: '18px',
        },
    }),
    contentWrap: css({
        flexDirection: 'row',
        alignItems: 'baseline',
        display: 'flex',
        position: 'relative',
        flexWrap: 'nowrap',
        marginRight: 'auto',
        marginLeft: 'auto',
        [breakpoints.tb]: {
            flexDirection: 'column',
            alignItems: 'unset',
        },
    }),
    splitColLeft: css({
        paddingRight: '32px',
        [breakpoints.tb]: {
            paddingRight: '0',
            marginBottom: '32px',

        },
    }),
    splitColRight: css({
        paddingLeft: '32px',
        [breakpoints.tb]: {
            paddingLeft: '0',
        },
    }),
    splitCol: css({
        display: 'flex',
        position: 'relative',
        flexDirection: 'column',
        margin: '0',
        width: '50%',
        flexShrink: 0,
        [breakpoints.tb]: {
            marginRight: 'auto',
            marginLeft: 'auto',
            flexShrink: 'unset',
            width: '100%',

        },
    }),
    copy: css({
        maxWidth: '768px',
        fontSize: '20px',
        lineHeight: '28px',
        marginBottom: '16px',
        'p a img': {
            width: '100%',
            maxWidth: '150px',
        },
        [breakpoints.tb]: {
            fontSize: '18px',
        },
    }),
    buttonWrap: css({
        display: 'flex',
        marginTop: '32px',
        [breakpoints.tb]: {
            marginTop: '32px',
        },
    }),
};

const SplitTextBanner: React.FC<Queries.SplitTextBannerDataFragment> = (data) => {
    const { settings, headlines, left, right } = data;

    if (!headlines || !left || !right) return null;

    const { headline: titleHeadline, subheadline: titleSubheadline } = headlines;
    const { headline: leftHeadline, copy: leftCopy, link: leftLink } = left;
    const { headline: rightHeadline, copy: rightCopy, link: rightLink } = right;

    const darkMode = !!settings?.darkMode;

    return (
        <section css={[{
            padding: '48px 0 0',
            overflow: 'hidden',
            background: darkMode ? colors.shadesBlack : undefined,
            color: darkMode ? colors.shadesWhite : undefined,
        }]}
        >
            <Container css={{
                position: 'relative',
                paddingTop: '4px',
            }}
            >
                {(titleHeadline || titleSubheadline) && (
                    <div css={{ marginBottom: '32px' }}>
                        <RowSet breakpoints={{
                            dt: { between: 16 },
                        }}
                        >
                            <Typography
                                as="h2"
                                fontWeight="regular"
                                reducedLetterSpacing
                                fontSize="3xl"
                                css={[{
                                    color: darkMode ? colors.shadesWhite : colors.shadesBlack,
                                    [breakpoints.tb]: {
                                        textAlign: 'center',
                                        maxWidth: '80%',
                                        margin: 'auto',
                                    },
                                }]}
                            >
                                {apCaseOnlyTitleTags(titleHeadline)}
                            </Typography>
                            {!!titleSubheadline && (
                                <Typography
                                    fontSize="xl"
                                    css={[RichTextEditorStyles, {
                                        color: darkMode ? colors.shadesWhite : undefined,
                                        lineHeight: '28px',
                                    }]}
                                    dangerouslySetInnerHTML={{
                                        __html: apCaseOnlyTitleTags(titleSubheadline),
                                    }}
                                />
                            )}
                        </RowSet>
                    </div>
                )}
            </Container>
            <Divider css={{
                opacity: darkMode ? 0.1 : 0.6,
            }}
            />
            <Container css={{
                position: 'relative',
                paddingTop: '32px',
                paddingBottom: '32px',
                [breakpoints.tb]: {
                    display: 'none',
                },
            }}
            >
                <Row>
                    <Col breakpoints={{ dt: { span: 6 } }}>
                        <div>
                            {leftHeadline && (
                                <Typography
                                    as="h3"
                                    color="primaryOrange"
                                    fontWeight="regular"
                                    fontSize={{
                                        dt: '3xl',
                                        tb: '2xl',
                                    }}
                                    css={{
                                        marginBottom: '24px',
                                    }}
                                >
                                    {apCaseOnlyTitleTags(leftHeadline)}
                                </Typography>
                            )}
                            <div
                                css={[RichTextEditorStyles, styles.copy]}
                                /* eslint-disable-next-line react/no-danger */
                                dangerouslySetInnerHTML={{ __html: leftCopy || '' }}
                            />
                            {!!leftLink?.href && (
                                <div css={styles.buttonWrap}>
                                    <StyledButtonAsLink to={leftLink.href}>{leftLink.title}</StyledButtonAsLink>
                                </div>
                            )}
                        </div>
                    </Col>
                    <Col breakpoints={{ dt: { span: 6 } }}>
                        {rightHeadline && (
                            <Typography
                                as="h3"
                                color="primaryOrange"
                                fontWeight="regular"
                                fontSize={{
                                    dt: '3xl',
                                    tb: '2xl',
                                }}
                                css={{
                                    marginBottom: '24px',
                                }}
                            >
                                {apCaseOnlyTitleTags(rightHeadline)}
                            </Typography>
                        )}
                        <div
                            css={[RichTextEditorStyles, styles.copy]}
                            /* eslint-disable-next-line react/no-danger */
                            dangerouslySetInnerHTML={{ __html: rightCopy || '' }}
                        />
                        {!!rightLink?.href && (
                            <div css={styles.buttonWrap}>
                                <StyledButtonAsLink to={rightLink.href}>{rightLink.title}</StyledButtonAsLink>
                            </div>
                        )}
                    </Col>
                </Row>
            </Container>
        </section>
    );
};

export const query = graphql`
  fragment SplitTextBannerData on CsSplitTextBanner {
    ... on CsSplitTextBanner {
      settings {
        darkMode
        hasBreadcrumbs
        hideBackground
      }
      headlines {
        headline
        subheadline
      }
      left {
        headline
        copy
        link {
          href
          title
        }
      }
      right {
        headline
        copy
        link {
          href
          title
        }
      }
    }
  }
`;

export default SplitTextBanner;
